import { computed, onMounted, ref, watch } from 'vue'

import { ApiListSubscription } from '@/api/ApiClientV2'

/**
 * Composable logic for handling row selection in BaseListTable component.
 * On mounted, when objectcollections or preSelectedRows are changed, rowSelections value is reset to default preSelectedRows property.
 * @param props of BaseListTable component
 * @returns rowSelections (state), selectedToEmit (computed), select (managment of rowSelections), isSelectable (check if row is selectable)
 */
export const useRowsSelection = (props: {
  singleSelection: boolean
  preSelectedRows: string[]
  unselectableRows: string[]
  objectCollections: ApiListSubscription<any> | null
}) => {
  const rowSelections = ref<Record<string, string>>({})

  const getSelected = () => {
    const selected = []
    for (const id in rowSelections.value) {
      if (rowSelections.value[id] && rowSelections.value[id] !== undefined) {
        selected.push(rowSelections.value[id])
      }
    }
    return selected
  }

  const selectedToEmit = computed(getSelected)

  const isSelectable = (rowId: string) =>
    !(props.unselectableRows.indexOf(rowId) > -1)

  const select = (row: any): void => {
    if (rowSelections.value[row.id] !== undefined) {
      rowSelections.value[row.id] = undefined
    } else {
      if (props.singleSelection) {
        for (const id in rowSelections.value) {
          if (rowSelections.value[id]) {
            rowSelections.value[id] = undefined
          }
        }
        // single selection: emit row
        rowSelections.value[row.id] = row
      } else {
        // no single selection: emid id
        rowSelections.value[row.id] = row.id
      }
    }
  }

  const resetRowSelectionsByDefault = () => {
    rowSelections.value = {}
    props.preSelectedRows.forEach((rowId: string) => {
      rowSelections.value[rowId] = rowId
    })
  }

  onMounted(() => {
    resetRowSelectionsByDefault()
  })

  watch(
    () => props.preSelectedRows,
    () => {
      resetRowSelectionsByDefault()
    }
  )

  watch(
    () => props.objectCollections,
    () => {
      resetRowSelectionsByDefault()
    }
  )

  return {
    rowSelections,
    selectedToEmit,
    isSelectable,
    select,
  }
}
